import { useContext, useEffect, useState } from "react";
import { AxiosResponse } from "axios";

import { forgetSettledAdds } from "@/components/chat/ChatCommerceCard";
import { CartContext } from "@/contexts/cartContext";
import type { ChatMessage } from "@/contexts/chatContext";
import {
  decodeLiveCard,
  decodeSavedBlockOffer,
  type CommerceCardBlock,
} from "@/services/commerceOffers";
import type { ProgramCardBlock } from "@/services/commerceProgramOffers";
import { postRequest } from "@/utils/axios";

// S4's server cap. It counts raw IDs, so the list is deduped first.
const MAX_OFFER_IDS = 6;

/**
 * A history session's fresh live cards, by offer_id. withdrawn: requested IDs the
 * response does not mention, so no longer offered or already in the cart. inCart: the
 * server's in-cart IDs; undefined when its list is missing or malformed.
 */
export type RefreshedCards = {
  sessionId: string;
  cards: ReadonlyMap<string, CommerceCardBlock | ProgramCardBlock>;
  withdrawn: ReadonlySet<string>;
  inCart?: ReadonlySet<string>;
};

/** One batch POST; throws when the request fails or the envelope is unreadable. */
const fetchRefreshedCards = async (
  offerIds: string[],
  signal: AbortSignal
): Promise<Omit<RefreshedCards, "sessionId">> => {
  const envelope = await postRequest(
    "/api/v1/ai/commerce-offers/",
    { offer_ids: offerIds },
    (response: AxiosResponse) => response.data,
    undefined,
    { signal }
  );
  if (envelope?.schema_version !== 1 || !Array.isArray(envelope.offers))
    throw new Error("Unreadable commerce offer envelope");
  const cards = new Map<string, CommerceCardBlock | ProgramCardBlock>();
  // cart_id is read per offer, so an empty response never binds a cart.
  for (const offer of envelope.offers as unknown[]) {
    const card = decodeLiveCard({
      type: "commerce_card",
      schema_version: 1,
      cart_id: envelope.cart_id,
      offer,
    });
    if (card) cards.set(card.offer.offer_id, card);
  }
  // A malformed entry still names its ID: unknown, so that card stays read-only.
  const mentioned = new Set(
    (envelope.offers as { offer_id?: unknown }[]).map(
      (offer) => offer?.offer_id
    )
  );
  const withdrawn = new Set(offerIds.filter((id) => !mentioned.has(id)));
  const inCartIds: unknown = envelope.in_cart_offer_ids;
  // Strict: anything but a list of strings leaves in-cart to this page's confirmed adds.
  const inCart =
    Array.isArray(inCartIds) &&
    inCartIds.every((id): id is string => typeof id === "string")
      ? new Set(inCartIds)
      : undefined;
  return { cards, withdrawn, inCart };
};

/**
 * Refreshes a history session's saved cards into live ones with one batch request.
 * Cards stay read-only until the refresh for this session, these IDs and this cart answers.
 */
export function useSavedCardRefresh(
  sessionId: string | null,
  messages: ChatMessage[],
  isVisible: boolean
): RefreshedCards | undefined {
  const { cart } = useContext(CartContext);
  const savedIds = messages
    .flatMap(({ blocks = [] }) => blocks)
    // History passes unknown block shapes through, so decode before reading.
    .flatMap((b) => decodeSavedBlockOffer(b)?.offer_id ?? []);
  // The newest cards win the capped slots. Offer IDs never contain commas.
  const idsKey = [...new Set(savedIds.reverse())]
    .slice(0, MAX_OFFER_IDS)
    .join(",");
  // A changed cart ID or line can change an offer or the cart a quote is bound to.
  const cartKey = JSON.stringify([cart?.id, cart?.line_items]);
  const requestKey = JSON.stringify([sessionId, idsKey, cartKey]);
  const [refreshed, setRefreshed] = useState<
    RefreshedCards & { requestKey: string }
  >();

  useEffect(() => {
    if (!isVisible) setRefreshed(undefined);
    if (!sessionId || !isVisible || !idsKey) return;
    // Every refresh starts from read-only cards, so no tap result outlives its quote.
    forgetSettledAdds(sessionId);
    const controller = new AbortController();
    fetchRefreshedCards(idsKey.split(","), controller.signal)
      .then((refresh) => {
        if (!controller.signal.aborted)
          setRefreshed({ requestKey, sessionId, ...refresh });
      })
      // Network, 4xx or 5xx (503 when subscriptions are unknown): stay read-only, no retry.
      .catch(() => {});
    return () => controller.abort();
  }, [requestKey, sessionId, idsKey, isVisible]);

  return refreshed?.requestKey === requestKey ? refreshed : undefined;
}
